import * as React from 'react';
import clsx from 'clsx';

interface ChatInputShellProps {
  /**
   * Border-gradient string for the outer pill. Defaults to the standard
   * accent gradient used by the logged-in chat input.
   */
  borderGradient?: string;
  /** Hide the desktop-only radial glow behind the input. Default false (glow shown). */
  hideGlow?: boolean;
  /**
   * When true, fades out the glow (used while a sidebar or echo transition is
   * in flight, or while recording). No effect when `hideGlow` is set.
   */
  glowFaded?: boolean;
  /** When true, lets the inner shell grow to fit the recording UI instead of locking 68px. */
  expandHeight?: boolean;
  /** Form wrapper classes (padding, width). */
  className?: string;
  /** Inner pill classes (rare — most callers just pass children). */
  innerClassName?: string;
  /** Disclaimer text rendered below the pill. Pass `null` to hide. */
  disclaimer?: React.ReactNode;
  onSubmit?: (e: React.FormEvent) => void;
  children: React.ReactNode;
}

const DEFAULT_BORDER_GRADIENT = 'linear-gradient(to right, #A28DF6, #6EDF21)';

/**
 * Shared wrapper for `ChatInput`, `PublicChatInput`, and `MockChatInput`.
 * Owns the gradient-border pill, the desktop radial glow, and the disclaimer
 * slot — the variants only differ in their inner widget (textarea + buttons,
 * or static placeholder, or themed fields).
 */
export const ChatInputShell: React.FC<ChatInputShellProps> = ({
  borderGradient = DEFAULT_BORDER_GRADIENT,
  hideGlow = false,
  glowFaded = false,
  expandHeight = false,
  className,
  innerClassName,
  disclaimer,
  onSubmit,
  children,
}) => (
  <form onSubmit={onSubmit} className={clsx('relative w-full px-4 pb-2', className)}>
    <div
      className={clsx('relative flex w-full items-center', innerClassName)}
      style={{
        height: expandHeight ? 'auto' : '68px',
        minHeight: '68px',
        borderRadius: '40px',
        border: '3px solid transparent',
        backgroundClip: 'padding-box, border-box',
        backgroundOrigin: 'padding-box, border-box',
        backgroundImage: `linear-gradient(#110D1E, #110D1E), ${borderGradient}`,
      }}
    >
      {!hideGlow && (
        <div
          className={clsx(
            'pointer-events-none absolute left-0 z-behind hidden w-full transition-opacity duration-300 md:block',
            glowFaded ? 'opacity-0' : 'opacity-100'
          )}
          style={{ top: '-500px', bottom: '0px', overflow: 'hidden', borderRadius: '40px' }}
        >
          <div
            className='absolute left-1/2 -translate-x-1/2'
            style={{
              width: '379px',
              height: '376px',
              bottom: '-160px',
              background:
                'radial-gradient(50% 50% at 50% 50%, rgba(69, 52, 158, 0.6) 0%, rgba(26, 16, 54, 0) 100%)',
              filter: 'blur(80px)',
              opacity: 1,
            }}
          />
        </div>
      )}

      {children}
    </div>

    {disclaimer}
  </form>
);

ChatInputShell.displayName = 'ChatInputShell';
